// The virtual screen of a designed room that has no screen object: one rule for the studio and the
// guest map, so operators see where guests will see it. Plan space (mm): the screen is centered on the
// wall at y = 0 and hangs from 900 mm. A room with an outline (CIN-15126) is as wide as its screen wall, not as
// its bounding box. Pure: relative imports only.
import { ceilingHeightAt } from './outline/ceiling.ts';
import { edgesOf } from './outline/outline.ts';
import type { RoomGeometry } from './outline/types.ts';

export const FALLBACK_SCREEN_BOTTOM_MM = 900;
const ASPECT = 16 / 9;
const SEAT_SPAN_FACTOR = 0.85;
const MIN_WIDTH_MM = 4000;
const WIDTH_OF_ROOM = 0.8;
const CEILING_CLEARANCE_MM = 400;
/** A screen never sits closer to the front row than this; a room with seats beyond the wall pushes the screen out. */
const MIN_FRONT_ROW_GAP_MM = 1000;

export type FallbackScreen = { x: number; y: number; width: number; bottom: number; height: number };

/** Length of the screen wall, mm: the screen edge of the outline, else the room's width. */
export function screenWallWidth(room: { width: number; geometry?: RoomGeometry | null }): number {
    const outline = room.geometry?.outline;
    const edge = outline ? edgesOf(outline).find((e) => e.isScreen) : undefined;
    return edge ? edge.chord : room.width;
}

/**
 * `seats` need only their plan `x`/`y`. Width is 16:9 sized from the seat span but never wider than
 * 80 % of the room, and never taller than the room allows.
 */
export function fallbackScreen(
    room: { width: number; height: number; depth?: number; geometry?: RoomGeometry | null },
    seats: { x: number; y: number }[]
): FallbackScreen {
    const maxWidth = screenWallWidth(room) * WIDTH_OF_ROOM;
    let width = maxWidth;
    let y = 0;
    if (seats.length > 0) {
        const xs = seats.map((s) => s.x);
        const span = Math.max(...xs) - Math.min(...xs);
        width = Math.min(maxWidth, Math.max(span * SEAT_SPAN_FACTOR, MIN_WIDTH_MM));
        y = Math.max(0, Math.max(...seats.map((s) => s.y)) + MIN_FRONT_ROW_GAP_MM);
    }
    let height = width / ASPECT;
    // The ceiling over the screen wall, which a sloped ceiling (CIN-15417) may keep lower than the room's top.
    const ceiling = ceilingHeightAt({ ...room, depth: room.depth ?? 1 }, 0);
    const maxHeight = ceiling - FALLBACK_SCREEN_BOTTOM_MM - CEILING_CLEARANCE_MM;
    if (maxHeight > 0 && height > maxHeight) {
        height = maxHeight;
        width = height * ASPECT;
    }
    return { x: 0, y, width, bottom: FALLBACK_SCREEN_BOTTOM_MM, height };
}
